/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2018, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import BaseStep from './BaseStep';

const SKIP_BADGEINFO_FOR_CATEGORIES = true;

class GetBadgesInfo extends BaseStep{

	_isVisTypeUnsupported(visId){
		const unsupportedVisTypes = [
			'com.ibm.vis.rave2comet',
			'com.ibm.vis.spiral',
			'com.ibm.vis.sunburst',
			'com.ibm.vis.decisiontree'
		];
		return unsupportedVisTypes.indexOf(visId) > -1;
	}

	process(renderContext) {
		return this.dashboardApi.getCanvasWhenReady().then( canvas => {
			this.content = canvas.getContent(this.ownerWidget.getId());
			// Skip the badges when we have a comet viz
			const visFeature = this.content.getFeature('Visualization');
			const visDefinition = visFeature && visFeature.getDefinition();
			const skipBadges = visDefinition && this._isVisTypeUnsupported(visDefinition.getId());
			const refreshData = renderContext.refresh && renderContext.refresh.data;
			//Do not ask for Badges if slots mappings are not compete or need to skip
			const slots = visFeature && visFeature.getSlots();
			if(!slots.isMappingComplete() || skipBadges){
				//In both case, there should not be any badges on the
				//widget, so we do clear badges info in these cases.
				this.badgesFeature.clearBadgesInfo();
				return Promise.resolve();
			} else if (!refreshData && this._isStepComplete(renderContext, 'getBadgeInfo')) {
				return Promise.resolve();
			}else{
				this.badgesFeature.clearBadgesInfo();
			}
			const badgeRenderContext = {
				requestMap:{}
			};
			const dataSlots = visFeature.getSlots();
			const processedDataItemIds = [];//Array that contains IDs of processed data items
			dataSlots.getMappedSlotList().forEach((slot, slotIndex) => {
				//For Endor release, confirmed with Predict team that Drivers Analytics for Categories are not supported
				//We disable requests for such category slots or attributes assigned to ordinal slots for performances
				const dataItems = slot.getDataItemList();
				dataItems.forEach( (dataItem) => {
					if(!this._skipRequestForDataItem(dataItem, processedDataItemIds)){
						const id = dataItem.getColumnId();
						const slotId = slot.getDefinition().getId();
						const requestId = slotId + '--' + id;
						// Skip the virtual multi measure series data item
						if (id !== '_multiMeasuresSeries' && !badgeRenderContext.requestMap[requestId]) {
							let badgeSlotIndex = -1;
							const tags = slot.getDefinition().getTags();
							if (tags && tags.indexOf('badge') !== -1) {
								badgeSlotIndex = slotIndex;
							}
							badgeRenderContext.requestMap[requestId]= this.getKeyDriverAnalysis(id, dataItem.getLabel(), badgeSlotIndex, slotId, renderContext);
						}
					}
				});
			});
			this._completeStep(renderContext, 'getBadgeInfo', badgeRenderContext);
			// resolve the step completion. This will start the execution of the rendering of the badges
			return Promise.resolve();
		});

	}

	_skipRequestForDataItem(dataItem, processedDataItemIds){
		const dataItemMetadataColumn = dataItem.getMetadataColumn && dataItem.getMetadataColumn();
		// if the dataItem is _multiMeasuresSeries, then getMetadataColumn will return null for it
		if(!dataItemMetadataColumn) {
			return true;
		}
		if(SKIP_BADGEINFO_FOR_CATEGORIES && dataItemMetadataColumn.getType() !== 'fact') {
			return true;
		}
		//There should be no badge requests for OLAP items.
		if (dataItemMetadataColumn.isOlapColumn()){
			return true;
		}

		const itemId = dataItem.getColumnId();
		//Only process the same dataItem ID once for the current widget
		if(processedDataItemIds.indexOf(itemId) > -1){
			return true;
		}else{
			//Cache this dataItem ID to the array
			processedDataItemIds.push(itemId);
		}

		return false;
	}

	getKeyDriverAnalysis(columnId, label, badgeSlotIndex, slotId, renderContext) {

		// Set the parameter "useModelData" to false when retrieving key drivers to get fresh metadata for badges
		// todo: use PredictServiceDataProviderAPI.whenPossibleKeyDriversAreReady when it is ready
		return this.visAPI.whenPossibleKeyDriversAreReady(renderContext, columnId, false /* useModelData */).then((possibleKeyDrivers) => {
			// Temporary until the live widget hooks up the smarts service caller
			// the current logic is returning all columns in the dataset
			let inputs;
			if(possibleKeyDrivers.recommendedDrivers) {
				inputs = possibleKeyDrivers.recommendedDrivers.map((entry) => {
					return entry.fieldIDForExpression;
				});
			} else {
				inputs = [];
			}

			// List of key drivers used for FPD in badges may bring many prompts from other columns that user don't know much about.
			// Pass empty promptControlFunctions to ignore prompts for badges.
			// todo: use PredictServiceDataProviderAPI.executeFastPatternDetectionRequest when it is ready
			return this.visAPI.executeFastPatternDetectionRequest(columnId, inputs, null /* method */, {} /* promptControlFunctions */).then((data) => {
				this.badgesFeature.setBadgeData(columnId, label, badgeSlotIndex, slotId, data);
				return data;
			}, (e) => {
				const errorMsg = 'Error running the predict request for the badge: '  + columnId;
				this.dashboardApi.getGlassCoreSvc('.Logger').error(errorMsg);
				const data = {
					'error': {
						message:errorMsg,
						rootCause: e
					}
				};
				this.badgesFeature.setBadgeData(columnId, label, badgeSlotIndex, slotId, data);
				return data;
			});
		});
	}
}

export default GetBadgesInfo;



// WEBPACK FOOTER //
// ./src/js/lib/@waca/vis-badges/src/js/rendersequence/GetBadgesInfo.js